/* Iron+Steel v3 product page: variant selection, gallery, stock line, add to bag, favorites, size guide. */

(() => {
  if (customElements.get('is-product')) return;

  const lockScroll = (on) => { document.documentElement.style.overflow = on ? 'hidden' : ''; };

  class ISProduct extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
      this.form = this.querySelector('[data-product-form]');
      this.idInput = this.querySelector('[data-variant-id]');
      this.qtyInput = this.querySelector('[data-qty-input]');
      this.addButtons = Array.from(this.querySelectorAll('[data-add]'));
      this.stock = this.querySelector('[data-stock]');
      this.error = this.querySelector('[data-error]');
      this.lowStock = parseInt(this.dataset.lowStock, 10) || 0;
      this.fieldsets = Array.from(this.querySelectorAll('[data-option]'));

      this.fieldsets.forEach((fs, i) => fs.addEventListener('change', () => this.onOptionChange(i)));
      this.querySelectorAll('[data-qty]').forEach((btn) =>
        btn.addEventListener('click', () => this.stepQty(parseInt(btn.dataset.qty, 10)))
      );
      this.querySelectorAll('[data-thumb]').forEach((thumb) =>
        thumb.addEventListener('click', () => this.showMedia(thumb.dataset.thumb))
      );
      this.querySelectorAll('[data-gallery-step]').forEach((btn) =>
        btn.addEventListener('click', () => this.stepMedia(parseInt(btn.dataset.galleryStep, 10)))
      );
      this.form.addEventListener('submit', (e) => this.onSubmit(e));

      this.bindFavorite();
      this.bindGuide();
      this.bindGallery();
      this.recordView();
      this.bindStickyBar();

      this.current = this.variants.find((v) => String(v.id) === this.idInput.value) || this.variants[0];
      this.bindNotify();
      try {
        this.initAge();
      } catch (err) {
        this.teardownAge();
      }
      this.initYouthAges();
      this.render(false);
    }

    /* Youth ages under the YOUTH size chips ("M" over "Age 12") from data-youth-ages on the Size
       fieldset (tees only, see is-main-product.liquid). Parents shop by age; a bare "M" sent
       youth tees back too big. Runs after initAge, which rewrites the chip text, and again after
       teardownAge restores it; chips that already show an age are left alone. */
    initYouthAges() {
      const fs = this.fieldsets.find((f) => f.dataset.youthAges);
      if (!fs) return;
      let ages;
      try {
        ages = JSON.parse(fs.dataset.youthAges);
      } catch (err) {
        return;
      }
      fs.querySelectorAll('input[type="radio"]').forEach((input) => {
        const m = String(input.value).trim().match(/^youth\s+(.+)$/i);
        const age = m && ages[m[1].trim().toUpperCase()];
        const label = age && fs.querySelector(`label[for="${input.id}"]`);
        if (!label || label.querySelector('.is-size__age')) return;
        const span = document.createElement('span');
        span.className = 'is-size__age';
        span.textContent = `Age ${age}`;
        label.classList.add('is-size--age');
        label.append(' ', span);
      });
    }

    /* Phone sticky Add to bag bar: publish its height and flag the page while it shows, so
       floating widgets (Smile's Rewards launcher) can move clear of it instead of covering the
       button. See is-product.css. Quick view has no bar and is left out. */
    bindStickyBar() {
      if (this.hasAttribute('data-no-url')) return;
      const bar = this.querySelector('[data-sticky]');
      if (!bar) return;
      const root = document.documentElement;
      const sync = () => {
        const h = bar.offsetHeight;
        root.classList.toggle('is-has-sticky-buy', h > 0);
        if (h > 0) root.style.setProperty('--is-sticky-buy-h', `${h}px`);
      };
      sync();
      if (window.ResizeObserver) {
        this.stickyObserver = new ResizeObserver(sync);
        this.stickyObserver.observe(bar);
      }
      window.addEventListener('resize', sync);
      this.onStickyResize = sync;
    }

    /* Recently viewed: the theme's recorder (Shopify.Products.recordRecentlyViewed in theme.js)
       writes the shopify_recently_viewed cookie. It used to be called by the old <product-info>
       element, which v3 product pages do not use, so nothing was recorded after launch. Quick
       view (data-no-url) is not a page visit and is left out. The recorder reads the handle
       from the URL; if theme.js has not run yet, try again on load. */
    recordView() {
      if (this.hasAttribute('data-no-url')) return;
      // Unlisted products (and any page Shopify marks noindex) are meant to be reached only by
      // their link, so they stay out of Recently viewed. Shopify adds this robots tag to every
      // unlisted product page; Liquid cannot read the status itself. A product unlisted after
      // someone viewed it is also taken out of their history here.
      const robots = document.querySelector('meta[name="robots"]');
      if (robots && /noindex/i.test(robots.getAttribute('content') || '')) {
        this.forgetView(this.dataset.productHandle);
        return;
      }
      const record = () => {
        const products = window.Shopify && window.Shopify.Products;
        if (!products || typeof products.recordRecentlyViewed !== 'function') return false;
        try {
          products.recordRecentlyViewed();
        } catch (err) {
          /* never let history break the product page */
        }
        return true;
      };
      if (!record()) window.addEventListener('load', record, {once: true});
    }

    // Drop one handle from the shopify_recently_viewed cookie, written with the same
    // attributes the theme's recorder uses so it replaces rather than duplicates it.
    forgetView(handle) {
      if (!handle) return;
      const name = 'shopify_recently_viewed';
      const raw = document.cookie.split('; ').find((c) => c.startsWith(`${name}=`));
      if (!raw) return;
      let list;
      try {
        list = decodeURIComponent(raw.slice(name.length + 1)).split(' ').filter(Boolean);
      } catch (err) {
        return;
      }
      if (!list.includes(handle)) return;
      const kept = list.filter((h) => h !== handle);
      const expires = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toUTCString();
      document.cookie = `${name}=${encodeURIComponent(kept.join(' '))}; expires=${expires}; path=/; domain=${window.location.hostname}; SameSite=None; Secure`;
    }

    disconnectedCallback() {
      // The section re-renders on variant changes in the editor; drop the document listener and
      // never leave the page scroll-locked behind a lightbox that no longer exists.
      if (this.onLightboxKey) document.removeEventListener('keydown', this.onLightboxKey);
      if (this.stickyObserver) this.stickyObserver.disconnect();
      if (this.onStickyResize) {
        window.removeEventListener('resize', this.onStickyResize);
        document.documentElement.classList.remove('is-has-sticky-buy');
      }
      if (this.lightbox && !this.lightbox.hidden) document.documentElement.style.overflow = '';
    }

    /* ---------- Age row (presentation only) ----------
       Products sold in two or more of Adult / Youth / Toddler get an Age row above Size, and the
       Size chips show the value without its prefix ("ADULT M" shows as "M"). The real Size radios
       and variant data are untouched, so the cart, orders and filters keep the full value. */
    static ageOf(value) {
      const v = String(value).trim();
      const m = v.match(/^(youth|adult)\s+(.+)$/i);
      if (m) return {age: m[1].toUpperCase(), label: m[2].trim()};
      if (/^(?:[2-6]T|5-6T)$/i.test(v)) return {age: 'TODDLER', label: v.toUpperCase()};
      return null;
    }

    initAge() {
      const fs = this.fieldsets.find((f) => f.dataset.optionName === 'size');
      if (!fs) return;
      const inputs = Array.from(fs.querySelectorAll('input[type="radio"]'));
      const parsed = inputs.map((input) => ISProduct.ageOf(input.value));
      if (!inputs.length || parsed.some((p) => !p)) return; // any unrecognized size: keep the normal picker
      // Youth leads: it is the larger audience, so it is listed first and selected on load.
      const groups = ['YOUTH', 'ADULT', 'TODDLER'].filter((g) => parsed.some((p) => p.age === g));
      if (groups.length < 2) return;

      const uid = `${this.idInput.form ? this.idInput.form.id : 'is'}-age`;
      const names = {ADULT: 'Adult', YOUTH: 'Youth', TODDLER: 'Toddler'};
      const ageFs = document.createElement('fieldset');
      ageFs.className = 'is-option is-age';
      ageFs.setAttribute('data-age', '');
      ageFs.innerHTML = `
        <div class="is-option__head"><legend class="is-label">Age</legend></div>
        <div class="is-option__values">
          ${groups.map((g, i) => `
            <input type="radio" id="${uid}-${i}" name="${uid}" value="${g}" form="${uid}-none">
            <label for="${uid}-${i}" class="is-size">${names[g]}</label>`).join('')}
        </div>`;

      const map = new Map();
      inputs.forEach((input, i) => {
        map.set(input.value, parsed[i]);
        const label = fs.querySelector(`label[for="${input.id}"]`);
        if (label) {
          label.dataset.fullLabel = label.textContent;
          label.textContent = parsed[i].label;
        }
      });

      fs.parentNode.insertBefore(ageFs, fs);
      this.age = {fs, ageFs, inputs, map, index: this.fieldsets.indexOf(fs)};
      ageFs.addEventListener('change', (e) => this.setAge(e.target.value, true));

      // Initial age: the variant asked for in the URL; else Youth, Adult, Toddler in that order,
      // skipping an age that is fully sold out in the current color (unless every age is).
      const params = new URLSearchParams(window.location.search);
      const fromUrl = !this.hasAttribute('data-no-url') && this.variants.some((v) => String(v.id) === params.get('variant'));
      const currentAge = this.ageOfVariant(this.current);
      const idx = this.age.index;
      const inStock = (g) => this.variants.some((v) => v.available && this.ageOfVariant(v) === g &&
        v.options.every((o, i) => i === idx || o === this.current.options[i]));
      const start = fromUrl ? currentAge : (groups.find(inStock) || groups[0]);
      this.ageSel = currentAge;
      if (start !== currentAge) {
        this.setAge(start, false, true);
        if (this.sizePending) {
          // First load (not a shopper's switch): start on the first in-stock size of that age.
          const first = this.variants.find((v) => v.available && this.ageOfVariant(v) === start &&
            v.options.every((o, i) => i === idx || o === this.current.options[i]));
          if (first) {
            this.current = first;
            this.sizePending = false;
          }
        }
      } else {
        this.showAge(start);
      }
    }

    teardownAge() {
      if (!this.age) return;
      this.age.ageFs.remove();
      this.age.inputs.forEach((input) => {
        input.hidden = false;
        const label = this.age.fs.querySelector(`label[for="${input.id}"]`);
        if (label) {
          label.hidden = false;
          if (label.dataset.fullLabel) label.textContent = label.dataset.fullLabel;
        }
      });
      const note = this.age.fs.querySelector('[data-youth-note]');
      if (note) note.hidden = false;
      this.setFit('adult');
      this.age = null;
      this.sizePending = false;
      this.initYouthAges();
    }

    ageOfVariant(v) {
      const p = this.age && this.age.map.get(v.options[this.age.index]);
      return p ? p.age : null;
    }

    showAge(age) {
      this.ageSel = age;
      this.age.ageFs.querySelectorAll('input').forEach((input) => { input.checked = input.value === age; });
      this.age.inputs.forEach((input) => {
        const hide = this.age.map.get(input.value).age !== age;
        input.hidden = hide;
        const label = this.age.fs.querySelector(`label[for="${input.id}"]`);
        if (label) label.hidden = hide;
      });
      const note = this.age.fs.querySelector('[data-youth-note]');
      if (note) note.hidden = age !== 'YOUTH';
      this.setFit(age === 'YOUTH' ? 'youth' : 'adult');
    }

    /* Fit meter on a youth + adult tee: the theme's Youth fit while youth sizes show, the
       product's own value otherwise (data-fit-youth / data-fit-adult, see is-main-product.liquid). */
    setFit(which) {
      const fit = this.querySelector('[data-fit]');
      if (!fit) return;
      const n = parseInt(which === 'youth' ? fit.dataset.fitYouth : fit.dataset.fitAdult, 10);
      if (!n) return;
      fit.querySelectorAll('.is-fit__scale span').forEach((s, i) => s.classList.toggle('is-on', i === n - 1));
      const labels = fit.querySelectorAll('.is-fit__labels span');
      [n < 3, n === 3, n > 3].forEach((on, i) => labels[i] && labels[i].classList.toggle('is-on', on));
    }

    setAge(age, fromUser, silent) {
      const idx = this.age.index;
      const currentSize = this.sizePending ? null : this.current.options[idx];
      const currentLabel = currentSize ? this.age.map.get(currentSize).label : null;
      this.showAge(age);
      if (currentSize && this.age.map.get(currentSize).age === age) {
        this.sizePending = false;
      } else {
        // Same size in the new age (S to S) keeps it; otherwise the shopper picks again.
        const target = currentLabel && this.age.inputs.find((input) => {
          const p = this.age.map.get(input.value);
          return p.age === age && p.label === currentLabel;
        });
        const match = target && this.variants.find((v) =>
          v.options.every((o, i) => (i === idx ? o === target.value : o === this.current.options[i])));
        if (match) {
          this.current = match;
          this.sizePending = false;
        } else {
          this.sizePending = true;
          // Price and photo for the chosen age while no size is picked. When the other picks do not
          // exist in that age (a youth-only Long Sleeve, say), they switch to the nearest that does.
          const stand = this.variants.find((v) =>
              this.ageOfVariant(v) === age && v.options.every((o, i) => i === idx || o === this.current.options[i])) ||
            this.closest((v) => this.ageOfVariant(v) === age, this.current.options);
          if (stand) this.current = stand;
        }
      }
      if (!silent) this.render(fromUser && !this.sizePending);
    }

    syncAge() {
      const {fs, ageFs, index} = this.age;
      if (!this.sizePending) {
        const age = this.ageOfVariant(this.current);
        if (age && age !== this.ageSel) this.showAge(age);
      } else {
        fs.querySelectorAll('input').forEach((input) => { input.checked = false; });
        this.addButtons.forEach((btn) => {
          btn.disabled = true;
          btn.textContent = 'Select a size';
        });
        this.stock.textContent = 'Select a size';
        if (this.notify) this.notify.hidden = true;
        const notifyLink = this.querySelector('[data-notify-link]');
        if (notifyLink) notifyLink.hidden = true;
        const stickyVariant = this.querySelector('[data-sticky-variant]');
        if (stickyVariant) {
          const others = this.current.options.filter((o, i) => i !== index);
          stickyVariant.textContent = [...others, 'Select a size'].join(' · ');
        }
      }
      // An age whose sizes are all sold out (for the other current selections) looks sold out but stays clickable.
      ageFs.querySelectorAll('input').forEach((input) => {
        const any = this.variants.some((v) => v.available && this.ageOfVariant(v) === input.value &&
          v.options.every((o, i) => i === index || o === this.current.options[i]));
        input.toggleAttribute('data-unavailable', !any);
      });
    }

    /* ---------- Variants ---------- */
    selectedOptions() {
      return this.fieldsets.map((fs) => {
        const checked = fs.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    /* The variant nearest the shopper's picks among those where keep(v) holds: the most options in
       common with `chosen` wins, then the same size letter in another age (Adult M to Youth M),
       an in-stock variant before a sold-out one, then variant order. */
    closest(keep, chosen) {
      const sizeLabel = (value) => {
        const p = this.age && value && this.age.map.get(value);
        return p ? p.label : null;
      };
      const wantLabel = this.age ? sizeLabel(chosen[this.age.index]) : null;
      let best = null;
      let bestScore = -1;
      this.variants.forEach((v) => {
        if (!keep(v)) return;
        const sameLabel = wantLabel && sizeLabel(v.options[this.age.index]) === wantLabel;
        const score = v.options.filter((o, i) => o === chosen[i]).length * 4 + (sameLabel ? 2 : 0) + (v.available ? 1 : 0);
        if (score > bestScore) {
          best = v;
          bestScore = score;
        }
      });
      return best;
    }

    onOptionChange(changed) {
      const chosen = this.selectedOptions();
      // The value the shopper just picked always survives. Without this, a value that only exists
      // with other picks (Long Sleeve comes in Black youth only) snapped straight back.
      const keepPick = (v) => changed == null || v.options[changed] === chosen[changed];
      if (this.age) {
        const idx = this.age.index;
        if (chosen[idx]) this.sizePending = false;
        if (this.sizePending) {
          // Another option changed while no size is picked: stay in the chosen age, keep waiting for a size.
          // If that age does not offer the pick, move to the nearest age that does.
          const stand = this.variants.find((v) => this.ageOfVariant(v) === this.ageSel &&
            v.options.every((o, i) => i === idx || o === chosen[i])) || this.closest(keepPick, chosen);
          if (stand) {
            this.current = stand;
            const age = this.ageOfVariant(stand);
            if (age && age !== this.ageSel) this.showAge(age);
          }
          this.render(false);
          return;
        }
      }
      const match = this.variants.find((v) => v.options.every((o, i) => o === chosen[i])) ||
        this.closest(keepPick, chosen) || this.variants[0];
      this.current = match;
      this.render(true);
    }

    render(updateUrl) {
      const v = this.current;
      const changed = this.idInput.value !== String(v.id);
      this.idInput.value = v.id;
      this.error.textContent = '';
      // Shopify's payment terms (installments) and back-in-stock apps listen to the variant id input.
      if (changed) this.idInput.dispatchEvent(new Event('change', {bubbles: true}));
      if (window._RestockRocketConfig) window._RestockRocketConfig.selected_variant_id = v.id;
      if (this.notify) this.notify.hidden = v.available || !window._RestockRocket;
      const notifyLink = this.querySelector('[data-notify-link]');
      if (notifyLink) {
        notifyLink.hidden = v.available;
        notifyLink.href = `${this.dataset.productUrl}?variant=${v.id}&out_of_stock=true`;
      }

      // Mark option values that cannot be bought with the other current selections.
      this.fieldsets.forEach((fs, index) => {
        fs.querySelectorAll('input').forEach((input) => {
          const reachable = this.variants.some(
            (variant) => variant.available && variant.options[index] === input.value &&
              variant.options.every((o, i) => i === index || o === v.options[i])
          );
          input.toggleAttribute('data-unavailable', !reachable);
          input.checked = v.options[index] === input.value;
        });
        const label = fs.querySelector('[data-option-value]');
        if (label) label.textContent = v.options[index];
      });

      // Price
      const price = this.querySelector('[data-price]');
      price.classList.toggle('is-buy__price--sale', !!v.compare);
      price.innerHTML = `<span class="is-buy__now">${v.price}</span>${v.compare ? `<s>${v.compare}</s>` : ''}`;

      // SKU
      const sku = this.querySelector('[data-sku]');
      if (sku) {
        sku.hidden = !v.sku;
        sku.textContent = v.sku ? `SKU ${v.sku}` : '';
      }

      // Stock line
      const sizeLabel = v.options[v.options.length - 1];
      if (!v.available) {
        this.stock.textContent = this.variants.length > 1 ? `${sizeLabel} is sold out, pick another size` : 'Sold out';
      } else if (this.lowStock > 0 && v.tracked && v.qty > 0 && v.qty <= this.lowStock) {
        this.stock.textContent = `Only ${v.qty} left${this.variants.length > 1 ? ` in ${sizeLabel}` : ''}`;
      } else {
        this.stock.textContent = 'In stock';
      }

      // Buttons
      this.addButtons.forEach((btn) => {
        btn.disabled = !v.available;
        btn.removeAttribute('data-state');
        const sticky = btn.closest('[data-sticky]');
        btn.textContent = v.available ? (sticky ? 'Add to bag' : `Add to bag - ${v.price}`) : 'Sold out';
      });

      const stickyVariant = this.querySelector('[data-sticky-variant]');
      if (stickyVariant) {
        stickyVariant.textContent = this.variants.length > 1 ? `${v.options.join(' · ')} · ${v.price}` : v.price;
      }

      if (v.media) this.showMedia(String(v.media));

      if (updateUrl && !this.hasAttribute('data-no-url')) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', url.toString());
      }

      if (this.age) {
        try {
          this.syncAge();
        } catch (err) {
          this.teardownAge();
        }
      }
    }

    stepQty(delta) {
      const next = Math.min(99, Math.max(1, (parseInt(this.qtyInput.value, 10) || 1) + delta));
      this.qtyInput.value = next;
    }

    /* ---------- Gallery ---------- */
    showMedia(id) {
      const slide = this.querySelector(`.is-gallery__slide[data-media-id="${id}"]`);
      if (!slide) return;
      this.querySelectorAll('.is-gallery__slide').forEach((s) => s.classList.toggle('is-active', s === slide));
      this.querySelectorAll('[data-thumb]').forEach((t) => t.setAttribute('aria-current', t.dataset.thumb === id ? 'true' : 'false'));

      const slides = Array.from(this.querySelectorAll('.is-gallery__slide'));
      const index = this.querySelector('[data-gallery-index]');
      if (index) index.textContent = String(slides.indexOf(slide) + 1);
      // Keep the lightbox on the same photo, so opening or closing it never jumps.
      this.syncLightbox(id);
    }

    stepMedia(dir) {
      const slides = Array.from(this.querySelectorAll('.is-gallery__slide'));
      if (!slides.length) return;
      const current = slides.findIndex((sl) => sl.classList.contains('is-active'));
      const next = slides[(current + dir + slides.length) % slides.length];
      this.showMedia(next.dataset.mediaId);
    }

    /* ---------- Gallery: swipe, keyboard, lightbox ---------- */
    bindGallery() {
      this.stage = this.querySelector('[data-gallery-stage]');
      this.lightbox = this.querySelector('[data-lightbox]');

      if (this.stage) {
        // A tap opens the lightbox; a drag past the threshold steps instead, so swiping never
        // fires the tap. Pointer events cover touch, pen and mouse in one path.
        let startX = 0;
        let startY = 0;
        let dragging = false;
        let moved = false;
        this.stage.addEventListener('pointerdown', (e) => {
          if (e.target.closest('button, a, video, iframe')) return;
          dragging = true;
          moved = false;
          startX = e.clientX;
          startY = e.clientY;
        });
        this.stage.addEventListener('pointermove', (e) => {
          if (!dragging) return;
          const dx = e.clientX - startX;
          if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(e.clientY - startY)) moved = true;
        });
        const end = (e) => {
          if (!dragging) return;
          dragging = false;
          const dx = e.clientX - startX;
          if (moved && Math.abs(dx) > 40) this.stepMedia(dx < 0 ? 1 : -1);
          else if (!moved && !e.target.closest('button, a, video, iframe')) this.openLightbox();
        };
        this.stage.addEventListener('pointerup', end);
        this.stage.addEventListener('pointercancel', () => { dragging = false; });

        this.stage.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight') { e.preventDefault(); this.stepMedia(1); }
          else if (e.key === 'ArrowLeft') { e.preventDefault(); this.stepMedia(-1); }
          else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.openLightbox(); }
        });
      }

      this.querySelectorAll('[data-lightbox-open]').forEach((b) =>
        b.addEventListener('click', (e) => { e.stopPropagation(); this.openLightbox(); })
      );
      if (!this.lightbox) return;

      this.querySelectorAll('[data-lightbox-close]').forEach((b) =>
        b.addEventListener('click', () => this.closeLightbox())
      );
      this.querySelectorAll('[data-lightbox-step]').forEach((b) =>
        b.addEventListener('click', () => this.stepLightbox(parseInt(b.dataset.lightboxStep, 10)))
      );

      this.onLightboxKey = (e) => {
        if (this.lightbox.hidden) return;
        if (e.key === 'Escape' && this.zoom) this.resetZoom();
        else if (e.key === 'Escape') this.closeLightbox();
        else if ((e.key === '+' || e.key === '=') && !this.zoom) this.toggleZoom();
        else if (e.key === '-' && this.zoom) this.resetZoom();
        else if (e.key === 'ArrowRight') this.stepLightbox(1);
        else if (e.key === 'ArrowLeft') this.stepLightbox(-1);
        else if (e.key === 'Tab') {
          // Keep focus inside the dialog while it is open.
          const focusables = this.lightbox.querySelectorAll('button');
          if (!focusables.length) return;
          const first = focusables[0];
          const last = focusables[focusables.length - 1];
          if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
          else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        }
      };
      document.addEventListener('keydown', this.onLightboxKey);

      const lbStage = this.lightbox.querySelector('[data-lightbox-stage]');
      if (lbStage) {
        let sx = 0;
        let sy = 0;
        let live = false;
        lbStage.addEventListener('pointerdown', (e) => {
          live = true; sx = e.clientX; sy = e.clientY;
          if (this.zoom) this.zoom.start = { x: e.clientX, y: e.clientY, ox: this.zoom.ox, oy: this.zoom.oy };
        });
        lbStage.addEventListener('pointermove', (e) => {
          if (!this.zoom) return;
          // A mouse looks around by pointing; a finger drags the photo.
          if (e.pointerType === 'mouse') this.panZoomTo(e.clientX, e.clientY);
          else if (live && this.zoom.start) this.dragZoom(e.clientX, e.clientY);
        });
        lbStage.addEventListener('pointerup', (e) => {
          if (!live) return;
          live = false;
          const dx = e.clientX - sx;
          const dy = e.clientY - sy;
          const tap = Math.abs(dx) < 8 && Math.abs(dy) < 8;
          if (tap && e.target.closest('.is-lb__img')) this.toggleZoom(e.clientX, e.clientY);
          else if (tap && this.zoom) this.resetZoom();
          else if (!this.zoom && Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) this.stepLightbox(dx < 0 ? 1 : -1);
        });
      }
      const zoomBtn = this.lightbox.querySelector('[data-lightbox-zoom]');
      if (zoomBtn) zoomBtn.addEventListener('click', () => this.toggleZoom());
    }

    /* ---------- Lightbox zoom ----------
       The photo scales up around a point (transform-origin), so panning is just moving that
       point. No animation: the brand keeps motion to color. The first zoom swaps in the 3000px
       file so fabric and print detail hold up at 2.5x. */
    activeLightboxImage() {
      return this.lightbox ? this.lightbox.querySelector('.is-lb__slide.is-active .is-lb__img') : null;
    }

    toggleZoom(x, y) {
      if (this.zoom) { this.resetZoom(); return; }
      const img = this.activeLightboxImage();
      if (!img) return;
      const rect = img.getBoundingClientRect();
      if (img.dataset.zoomSrc && !img.dataset.zoomLoaded) {
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.src = img.dataset.zoomSrc;
        img.dataset.zoomLoaded = '1';
      }
      this.zoom = { img, rect, ox: 50, oy: 50, start: null };
      if (x != null) this.panZoomTo(x, y); else this.applyZoom();
      img.style.transform = 'scale(2.5)';
      img.closest('.is-lb__slide').classList.add('is-zoomed');
      this.lightbox.querySelector('[data-lightbox-stage]').classList.add('is-zooming');
      this.setZoomButton(true);
    }

    panZoomTo(x, y) {
      const z = this.zoom;
      z.ox = Math.min(100, Math.max(0, ((x - z.rect.left) / z.rect.width) * 100));
      z.oy = Math.min(100, Math.max(0, ((y - z.rect.top) / z.rect.height) * 100));
      this.applyZoom();
    }

    dragZoom(x, y) {
      const z = this.zoom;
      // Dragging right shows more of the left side, so the origin moves against the finger.
      z.ox = Math.min(100, Math.max(0, z.start.ox - ((x - z.start.x) / z.rect.width) * 100 / 1.5));
      z.oy = Math.min(100, Math.max(0, z.start.oy - ((y - z.start.y) / z.rect.height) * 100 / 1.5));
      this.applyZoom();
    }

    applyZoom() {
      if (this.zoom) this.zoom.img.style.transformOrigin = `${this.zoom.ox}% ${this.zoom.oy}%`;
    }

    resetZoom() {
      if (!this.zoom) return;
      const { img } = this.zoom;
      img.style.transform = '';
      img.style.transformOrigin = '';
      const slide = img.closest('.is-lb__slide');
      if (slide) slide.classList.remove('is-zoomed');
      const stage = this.lightbox && this.lightbox.querySelector('[data-lightbox-stage]');
      if (stage) stage.classList.remove('is-zooming');
      this.zoom = null;
      this.setZoomButton(false);
    }

    setZoomButton(on) {
      const b = this.lightbox && this.lightbox.querySelector('[data-lightbox-zoom]');
      if (!b) return;
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
      b.setAttribute('aria-label', on ? 'Zoom out' : 'Zoom in');
    }

    lightboxSlides() {
      return this.lightbox ? Array.from(this.lightbox.querySelectorAll('.is-lb__slide')) : [];
    }

    // Mirror the gallery's active photo into the lightbox. Videos have no lightbox slide, so
    // the nearest image stays selected rather than leaving the dialog blank.
    syncLightbox(id) {
      const slides = this.lightboxSlides();
      if (!slides.length) return;
      this.resetZoom();
      const match = slides.find((s) => s.dataset.lbMedia === String(id));
      const target = match || slides.find((s) => s.classList.contains('is-active')) || slides[0];
      slides.forEach((s) => s.classList.toggle('is-active', s === target));
      const index = this.lightbox.querySelector('[data-lightbox-index]');
      if (index) index.textContent = String(slides.indexOf(target) + 1);
      const total = this.lightbox.querySelector('[data-lightbox-total]');
      if (total) total.textContent = String(slides.length);
    }

    openLightbox() {
      if (!this.lightbox || !this.lightboxSlides().length) return;
      const active = this.querySelector('.is-gallery__slide.is-active');
      this.syncLightbox(active ? active.dataset.mediaId : null);
      this.lightboxReturn = document.activeElement;
      this.lightbox.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      const close = this.lightbox.querySelector('[data-lightbox-close]');
      if (close) setTimeout(() => close.focus(), 40);
    }

    closeLightbox() {
      if (!this.lightbox || this.lightbox.hidden) return;
      this.resetZoom();
      this.lightbox.hidden = true;
      document.documentElement.style.overflow = '';
      // Hand the gallery back the photo the visitor ended on.
      const active = this.lightboxSlides().find((s) => s.classList.contains('is-active'));
      if (active) this.showMedia(active.dataset.lbMedia);
      if (this.lightboxReturn && this.lightboxReturn.focus) this.lightboxReturn.focus();
    }

    stepLightbox(dir) {
      const slides = this.lightboxSlides();
      if (slides.length < 2) return;
      const i = slides.findIndex((s) => s.classList.contains('is-active'));
      const next = slides[(i + dir + slides.length) % slides.length];
      this.syncLightbox(next.dataset.lbMedia);
    }

    /* ---------- Add to bag ---------- */
    async onSubmit(e) {
      e.preventDefault();
      if (this.sizePending) {
        this.error.textContent = 'Select a size first.';
        return;
      }
      if (!this.current.available) return;
      this.error.textContent = '';
      this.addButtons.forEach((b) => b.setAttribute('aria-busy', 'true'));

      try {
        const res = await fetch(`${window.theme?.routes?.cart_add_url || '/cart/add'}.js`, {
          method: 'POST',
          headers: {'Content-Type': 'application/json', Accept: 'application/json'},
          body: JSON.stringify({items: [{id: this.current.id, quantity: parseInt(this.qtyInput.value, 10) || 1}]}),
        });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || 'That could not be added.');

        this.addButtons.forEach((b) => {
          b.setAttribute('data-state', 'added');
          b.textContent = 'Added to bag';
        });
        setTimeout(() => this.render(false), 2400);
        this.dispatchEvent(new CustomEvent('is:product:added', {bubbles: true, detail: {id: this.current.id}}));

        // The theme cart drawer re-renders and opens on this event (cart page falls back to a redirect).
        if (window.theme?.settings?.cartType === 'drawer' && document.querySelector('[data-cart-drawer]')) {
          document.dispatchEvent(new CustomEvent('theme:cart:refresh', {bubbles: true}));
        } else {
          window.location = window.theme?.routes?.cart_url || '/cart';
        }
      } catch (err) {
        this.error.textContent = err.message;
      } finally {
        this.addButtons.forEach((b) => b.removeAttribute('aria-busy'));
      }
    }

    /* ---------- Back in stock (STOQ): inline button next to Sold out ---------- */
    bindNotify() {
      this.notify = this.querySelector('[data-notify]');
      if (!this.notify) return;
      const sync = () => {
        this.notify.hidden = this.current.available || !window._RestockRocket;
        document.documentElement.classList.toggle('is-has-notify', !!window._RestockRocket);
      };
      this.notify.addEventListener('click', () => {
        if (window._RestockRocketConfig) window._RestockRocketConfig.selected_variant_id = this.current.id;
        if (window._RestockRocket?.openModal) window._RestockRocket.openModal();
      });
      // STOQ loads async; check again once it is ready.
      sync();
      [800, 2000, 4000].forEach((ms) => setTimeout(sync, ms));
    }

    /* ---------- Favorites ---------- */
    bindFavorite() {
      this.fav = this.querySelector('[data-fav]');
      this.favNote = this.querySelector('[data-fav-note]');
      if (!this.fav || !window.ISWishlist) return;
      const handle = this.dataset.productHandle;
      const sync = () => {
        const saved = window.ISWishlist.read().includes(handle);
        this.fav.setAttribute('aria-pressed', saved ? 'true' : 'false');
        this.fav.setAttribute('aria-label', saved ? 'Remove from favorites' : 'Save to favorites');
      };
      this.fav.addEventListener('click', () => {
        const list = window.ISWishlist.read();
        const saved = list.includes(handle);
        if (window.ISWishlist.setVariant) window.ISWishlist.setVariant(handle, saved ? null : this.current && this.current.id);
        window.ISWishlist.write(saved ? list.filter((h) => h !== handle) : [...list, handle]);
        this.favNote.textContent = saved ? 'Removed from your favorites' : 'Saved to your favorites';
        sync();
      });
      document.addEventListener('is:wishlist:change', sync);
      sync();
    }

    /* ---------- Size guide drawer ---------- */
    bindGuide() {
      this.guide = this.querySelector('[data-guide]');
      if (!this.guide) return;
      this.scrim = this.querySelector('.is-sheet-scrim');
      this.querySelectorAll('[data-guide-open]').forEach((b) => b.addEventListener('click', (e) => {
        this.guideTrigger = e.currentTarget;
        this.openGuide();
      }));
      this.querySelectorAll('[data-guide-close]').forEach((b) => b.addEventListener('click', () => this.closeGuide()));
      this.guide.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.closeGuide(); });
      this.guide.querySelectorAll('[data-tab]').forEach((tab) => tab.addEventListener('click', () => {
        this.guide.querySelectorAll('[data-tab]').forEach((t) => t.setAttribute('aria-selected', t === tab ? 'true' : 'false'));
        this.guide.querySelectorAll('[data-panel]').forEach((p) => { p.hidden = p.dataset.panel !== tab.dataset.tab; });
      }));
    }

    openGuide() {
      this.guide.setAttribute('data-open', '');
      this.scrim.setAttribute('data-open', '');
      lockScroll(true);
      setTimeout(() => this.guide.querySelector('.is-sheet__close').focus(), 60);
    }

    closeGuide() {
      if (!this.guide.hasAttribute('data-open')) return;
      this.guide.removeAttribute('data-open');
      this.scrim.removeAttribute('data-open');
      lockScroll(false);
      if (this.guideTrigger) this.guideTrigger.focus();
    }
  }

  customElements.define('is-product', ISProduct);
})();
